import { useAppConfig } from '@plato/app-core/hooks/use-app-config';
import { useIsCustomerMode } from '@plato/app-core/hooks/use-customer-mode';
import { SalesMetricBasis } from '@plato/app-meta/sales-metric';
import { CurrencyOrEmpty } from '@plato/app-shared/components/currency-or-empty';
import { MaskedContent } from '@plato/app-shared/components/masked-content';
import { MetricInfoHint } from '@plato/app-shared/components/metric-explainer';
import { DateText } from '@plato/ui-legacy/components/common/date-time-text';
import { valueFormatter } from '@plato/ui-legacy/lib/chart-helpers';
import { aDateWithTheYearLastYear } from '@plato/ui-legacy/lib/date-helpers';
import { useResolvedLocale } from '@plato/ui-legacy/lib/hooks/locale';
import { AreaChart } from '@tremor/react';
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { ExplorerViewFilters } from '../../../server/utils/explorer-utils.ts';
import { emptyExplorerViewFilters } from '../../../server/utils/explorer-utils.ts';
import { api } from '../../../utils/api.ts';
import { useAnalytics } from '../../analytics/hooks/use-analytics.ts';
import { ChartLegend } from '../../charts/components/chart-legend.tsx';
import { ChartLoading } from '../../charts/components/chart-loading.tsx';
import type { YearComparisonChartTooltipProps } from '../../charts/components/year-comparison-chart-tooltip.tsx';
import { YearComparisonChartTooltip } from '../../charts/components/year-comparison-chart-tooltip.tsx';
import { useChartYAxisWidth } from '../../charts/hooks/use-chart-y-axis-width.tsx';
import { mapChartData } from '../../charts/utils/chart-utils.ts';
import { DashboardChatTabs, DashboardChatTabsItem } from '../../dashboard/components/dashboard-chat-tabs.tsx';
import { DashboardSection } from '../../dashboard/components/dashboard-section.tsx';
import { DataStatBlock } from '../../dashboard/components/data-stat-block.tsx';
import { PercentageDeltaBadge } from '../../insights-common/components/percentage-delta-badge.tsx';

export const ChartTab = {
    ORDER_SUM: 'ORDER_SUM',
    REVENUE: 'REVENUE',
    MARGIN: 'MARGIN',
    ORDER_SUM_MARGIN: 'ORDER_SUM_MARGIN',
} as const;

export type ChartTab = (typeof ChartTab)[keyof typeof ChartTab];

type Props = {
    overrideFilters?: Partial<ExplorerViewFilters>;
    hideTitles?: boolean;
    onReady?: () => void;
};

const chartTabToBillingStatus: Record<ChartTab, SalesMetricBasis> = {
    [ChartTab.REVENUE]: SalesMetricBasis.BILLED,
    [ChartTab.ORDER_SUM]: SalesMetricBasis.ORDER_SUM,
    [ChartTab.MARGIN]: SalesMetricBasis.BILLED,
    [ChartTab.ORDER_SUM_MARGIN]: SalesMetricBasis.ORDER_SUM,
};

export function ExplorerViewChart({ overrideFilters, hideTitles, onReady }: Props): ReactNode {
    const { t } = useTranslation();
    const locale = useResolvedLocale();
    const sendEvent = useAnalytics();
    const { defaultCurrency, canReadGrossMargin, defaultMetric, defaultTimeframe } = useAppConfig();

    const defaultChartTab = defaultMetric === 'Order_Sum' ? ChartTab.ORDER_SUM : ChartTab.REVENUE;

    const [selectedChartTab, setSelectedChartTab] = useState<ChartTab>(defaultChartTab);

    // Picking a new default metric in the settings menu leaves this chart mounted, and
    // `useState` seeds on the first render only, so the tab would not move. Re-seed only when
    // the default actually changes, so a tab the user clicks by hand still sticks.
    const [prevDefaultChartTab, setPrevDefaultChartTab] = useState(defaultChartTab);
    if (defaultChartTab !== prevDefaultChartTab) {
        setPrevDefaultChartTab(defaultChartTab);
        setSelectedChartTab(defaultChartTab);
    }

    const filters = useMemo(
        () => ({
            ...emptyExplorerViewFilters,
            ...overrideFilters,
        }),
        [overrideFilters],
    );

    const billingStatus = chartTabToBillingStatus[selectedChartTab];
    const isRollingTimeframe = defaultTimeframe === 'TTM';

    const customerViewToggled = useIsCustomerMode();
    const { yAxisWidth } = useChartYAxisWidth();

    const summary = api.platoUser.query.explorer.getExplorerSummary.getExplorerSummaryWithTimeframe.useQuery({
        filters,
        billingStatus,
        timeframe: defaultTimeframe,
    });

    const chart = api.platoUser.query.explorer.getExplorerSummary.getChartDataWithTimeframe.useQuery({
        filters,
        billingStatus,
        timeframe: defaultTimeframe,
    });

    const summaryData = summary.data;
    const loadingSummaryData = summary.isLoading;
    const isSummarySuccess = summary.isSuccess;

    const data = chart.data;
    const isChartDataSuccess = chart.isSuccess;

    useEffect(() => {
        if (isSummarySuccess && isChartDataSuccess) {
            onReady?.();
        }
    }, [isSummarySuccess, isChartDataSuccess, onReady]);

    const revenueChartData = useMemo(() => {
        if (data?.revenueChartData) {
            return mapChartData(data.revenueChartData, data.thisYear, locale);
        }
        return null;
    }, [data, locale]);

    const marginChartData = useMemo(() => {
        if (data?.marginChartData) {
            return mapChartData(data.marginChartData, data.thisYear, locale);
        }
        return null;
    }, [data, locale]);

    const thisYearAsNumber = data?.thisYear;
    const lastYearAsNumber = data?.lastYear;

    const currentSeriesLabel = isRollingTimeframe ? t('last_12_months') : thisYearAsNumber?.toString();
    const comparisonSeriesLabel = isRollingTimeframe ? t('previous_12_months') : lastYearAsNumber?.toString();

    const sharedTooltipProps = {
        differenceLabel: t('difference'),
    } satisfies Partial<YearComparisonChartTooltipProps>;

    return (
        <DashboardSection>
            <DashboardChatTabs
                key={selectedChartTab}
                defaultTab={selectedChartTab}
                onTabChange={(tab) => {
                    const nextTab = tab as ChartTab;
                    if (nextTab !== selectedChartTab) {
                        sendEvent('explorer_chart_tab_changed', {
                            from: selectedChartTab,
                            to: nextTab,
                        });
                    }
                    setSelectedChartTab(nextTab);
                }}
            >
                <DashboardChatTabsItem
                    className="flex flex-col"
                    tabId={ChartTab.ORDER_SUM}
                    label={t('radio_option__order_sum', { ns: 'explorer' })}
                    detailBlock={
                        <DataStatBlock.NoBorder>
                            {!hideTitles && (
                                <DataStatBlock.Title className="flex items-center gap-tight">
                                    {t('section_title__order_sum_total', { ns: 'dashboard' })}
                                    <MetricInfoHint metric={SalesMetricBasis.ORDER_SUM} />
                                </DataStatBlock.Title>
                            )}
                            <DataStatBlock.Body isLoading={loadingSummaryData}>
                                <DataStatBlock.BodyRow>
                                    <span>
                                        <CurrencyOrEmpty>{summaryData?.total_amount_current}</CurrencyOrEmpty>
                                    </span>

                                    <PercentageDeltaBadge>
                                        {summaryData?.total_amount_delta_percentage}
                                    </PercentageDeltaBadge>

                                    <DataStatBlock.SubTitle>{` ${isRollingTimeframe ? t('vs_previous_12_months') : t('vs_lytd')}`}</DataStatBlock.SubTitle>
                                </DataStatBlock.BodyRow>
                            </DataStatBlock.Body>
                            <DataStatBlock.Footer>
                                <span>
                                    <CurrencyOrEmpty>
                                        {summaryData?.total_amount_full_previous_period}
                                    </CurrencyOrEmpty>
                                    {isRollingTimeframe ? (
                                        ` ${t('previous_12_months')}`
                                    ) : (
                                        <>
                                            {` ${t('total_amount')} `}
                                            <DateText
                                                date={aDateWithTheYearLastYear}
                                                format={{ year: 'numeric' }}
                                            />
                                        </>
                                    )}
                                </span>
                            </DataStatBlock.Footer>
                        </DataStatBlock.NoBorder>
                    }
                >
                    {revenueChartData ? (
                        <>
                            <ChartLegend
                                currentLabel={currentSeriesLabel}
                                comparisonLabel={comparisonSeriesLabel}
                            />
                            <AreaChart
                                yAxisWidth={yAxisWidth}
                                className="bg-tremor-background h-44 xl:h-72"
                                showLegend={false}
                                data={revenueChartData}
                                index="month"
                                categories={['lastYear', 'thisYear']}
                                colors={['tremor-chart-secondary', 'tremor-chart-primary']}
                                valueFormatter={(val) => valueFormatter(val, locale, defaultCurrency)}
                                customTooltip={(props) => (
                                    <YearComparisonChartTooltip
                                        {...sharedTooltipProps}
                                        {...props}
                                        label={t('radio_option__order_sum', { ns: 'explorer' })}
                                        valueDisplayType="currency"
                                    />
                                )}
                                curveType="monotone"
                            />
                        </>
                    ) : (
                        <ChartLoading />
                    )}
                </DashboardChatTabsItem>
                <DashboardChatTabsItem
                    className="flex flex-col"
                    tabId={ChartTab.REVENUE}
                    label={t('radio_option__revenue', { ns: 'explorer' })}
                    detailBlock={
                        <DataStatBlock.NoBorder>
                            {!hideTitles && (
                                <DataStatBlock.Title className="flex items-center gap-tight">
                                    {t('section_title__revenue_invoiced', { ns: 'dashboard' })}
                                    <MetricInfoHint metric={SalesMetricBasis.BILLED} />
                                </DataStatBlock.Title>
                            )}
                            <DataStatBlock.Body isLoading={loadingSummaryData}>
                                <DataStatBlock.BodyRow>
                                    <span>
                                        <CurrencyOrEmpty>{summaryData?.total_amount_current}</CurrencyOrEmpty>
                                    </span>

                                    <PercentageDeltaBadge>
                                        {summaryData?.total_amount_delta_percentage}
                                    </PercentageDeltaBadge>

                                    <DataStatBlock.SubTitle>{` ${isRollingTimeframe ? t('vs_previous_12_months') : t('vs_lytd')}`}</DataStatBlock.SubTitle>
                                </DataStatBlock.BodyRow>
                            </DataStatBlock.Body>
                            <DataStatBlock.Footer>
                                <span>
                                    <CurrencyOrEmpty>
                                        {summaryData?.total_amount_full_previous_period}
                                    </CurrencyOrEmpty>
                                    {isRollingTimeframe ? (
                                        ` ${t('previous_12_months')}`
                                    ) : (
                                        <>
                                            {` ${t('total_amount')} `}
                                            <DateText
                                                date={aDateWithTheYearLastYear}
                                                format={{ year: 'numeric' }}
                                            />
                                        </>
                                    )}
                                </span>
                            </DataStatBlock.Footer>
                        </DataStatBlock.NoBorder>
                    }
                >
                    {revenueChartData ? (
                        <>
                            <ChartLegend
                                currentLabel={currentSeriesLabel}
                                comparisonLabel={comparisonSeriesLabel}
                            />
                            <AreaChart
                                yAxisWidth={yAxisWidth}
                                className="bg-tremor-background h-44 xl:h-72"
                                showLegend={false}
                                data={revenueChartData}
                                index="month"
                                categories={['lastYear', 'thisYear']}
                                colors={['tremor-chart-secondary', 'tremor-chart-primary']}
                                valueFormatter={(val) => valueFormatter(val, locale, defaultCurrency)}
                                customTooltip={(props) => (
                                    <YearComparisonChartTooltip
                                        {...sharedTooltipProps}
                                        {...props}
                                        label={t('radio_option__revenue', { ns: 'explorer' })}
                                        valueDisplayType="currency"
                                    />
                                )}
                                curveType="monotone"
                            />
                        </>
                    ) : (
                        <ChartLoading />
                    )}
                </DashboardChatTabsItem>
                {canReadGrossMargin && (
                    <DashboardChatTabsItem
                        className="flex flex-col"
                        tabId={ChartTab.MARGIN}
                        label={t('radio_option__margin', { ns: 'explorer' })}
                        detailBlock={
                            <DataStatBlock.NoBorder>
                                {!hideTitles && (
                                    <DataStatBlock.Title className="flex items-center gap-tight">
                                        {t('section_title__margin_invoiced', { ns: 'dashboard' })}
                                        <MetricInfoHint
                                            base={SalesMetricBasis.BILLED}
                                            metric="GROSS_MARGIN"
                                        />
                                    </DataStatBlock.Title>
                                )}
                                <DataStatBlock.Body isLoading={loadingSummaryData}>
                                    <DataStatBlock.BodyRow>
                                        <MaskedContent>
                                            <span>
                                                <CurrencyOrEmpty>
                                                    {summaryData?.gross_margin_current}
                                                </CurrencyOrEmpty>
                                            </span>

                                            <PercentageDeltaBadge>
                                                {summaryData?.gross_margin_delta_percentage}
                                            </PercentageDeltaBadge>

                                            <DataStatBlock.SubTitle>
                                                {` ${isRollingTimeframe ? t('vs_previous_12_months') : t('vs_lytd')}`}
                                            </DataStatBlock.SubTitle>
                                        </MaskedContent>
                                    </DataStatBlock.BodyRow>
                                </DataStatBlock.Body>
                                <DataStatBlock.Footer>
                                    <MaskedContent>
                                        <CurrencyOrEmpty>
                                            {summaryData?.gross_margin_full_previous_period}
                                        </CurrencyOrEmpty>
                                        {isRollingTimeframe ? (
                                            ` ${t('previous_12_months')}`
                                        ) : (
                                            <>
                                                {` ${t('total_amount')} `}
                                                <DateText
                                                    date={aDateWithTheYearLastYear}
                                                    format={{ year: 'numeric' }}
                                                />
                                            </>
                                        )}
                                    </MaskedContent>
                                </DataStatBlock.Footer>
                            </DataStatBlock.NoBorder>
                        }
                    >
                        {marginChartData ? (
                            <>
                                <ChartLegend
                                    currentLabel={currentSeriesLabel}
                                    comparisonLabel={comparisonSeriesLabel}
                                />
                                <AreaChart
                                    yAxisWidth={yAxisWidth}
                                    className="bg-tremor-background h-44 xl:h-72"
                                    showLegend={false}
                                    data={marginChartData}
                                    index="month"
                                    categories={['lastYear', 'thisYear']}
                                    colors={['tremor-chart-secondary', 'tremor-chart-primary']}
                                    valueFormatter={(val) =>
                                        customerViewToggled
                                            ? '*****'
                                            : valueFormatter(val, locale, defaultCurrency)
                                    }
                                    customTooltip={(props) => (
                                        <YearComparisonChartTooltip
                                            {...sharedTooltipProps}
                                            {...props}
                                            hideAmounts={customerViewToggled}
                                            label={t('radio_option__margin', { ns: 'explorer' })}
                                            valueDisplayType="currency"
                                        />
                                    )}
                                    curveType="monotone"
                                />
                            </>
                        ) : (
                            <ChartLoading />
                        )}
                    </DashboardChatTabsItem>
                )}
                {canReadGrossMargin && (
                    <DashboardChatTabsItem
                        className="flex flex-col"
                        tabId={ChartTab.ORDER_SUM_MARGIN}
                        label={t('radio_option__order_sum_margin', { ns: 'explorer' })}
                        detailBlock={
                            <DataStatBlock.NoBorder>
                                {!hideTitles && (
                                    <DataStatBlock.Title className="flex items-center gap-tight">
                                        {t('section_title__margin_order_sum', { ns: 'dashboard' })}
                                        <MetricInfoHint
                                            base={SalesMetricBasis.ORDER_SUM}
                                            metric="GROSS_MARGIN"
                                        />
                                    </DataStatBlock.Title>
                                )}
                                <DataStatBlock.Body isLoading={loadingSummaryData}>
                                    <DataStatBlock.BodyRow>
                                        <MaskedContent>
                                            <span>
                                                <CurrencyOrEmpty>
                                                    {summaryData?.gross_margin_current}
                                                </CurrencyOrEmpty>
                                            </span>

                                            <PercentageDeltaBadge>
                                                {summaryData?.gross_margin_delta_percentage}
                                            </PercentageDeltaBadge>

                                            <DataStatBlock.SubTitle>
                                                {` ${isRollingTimeframe ? t('vs_previous_12_months') : t('vs_lytd')}`}
                                            </DataStatBlock.SubTitle>
                                        </MaskedContent>
                                    </DataStatBlock.BodyRow>
                                </DataStatBlock.Body>
                                <DataStatBlock.Footer>
                                    <MaskedContent>
                                        <CurrencyOrEmpty>
                                            {summaryData?.gross_margin_full_previous_period}
                                        </CurrencyOrEmpty>
                                        {isRollingTimeframe ? (
                                            ` ${t('previous_12_months')}`
                                        ) : (
                                            <>
                                                {` ${t('total_amount')} `}
                                                <DateText
                                                    date={aDateWithTheYearLastYear}
                                                    format={{ year: 'numeric' }}
                                                />
                                            </>
                                        )}
                                    </MaskedContent>
                                </DataStatBlock.Footer>
                            </DataStatBlock.NoBorder>
                        }
                    >
                        {marginChartData ? (
                            <>
                                <ChartLegend
                                    currentLabel={currentSeriesLabel}
                                    comparisonLabel={comparisonSeriesLabel}
                                />
                                <AreaChart
                                    yAxisWidth={yAxisWidth}
                                    className="bg-tremor-background h-44 xl:h-72"
                                    showLegend={false}
                                    data={marginChartData}
                                    index="month"
                                    categories={['lastYear', 'thisYear']}
                                    colors={['tremor-chart-secondary', 'tremor-chart-primary']}
                                    valueFormatter={(val) =>
                                        customerViewToggled
                                            ? '*****'
                                            : valueFormatter(val, locale, defaultCurrency)
                                    }
                                    customTooltip={(props) => (
                                        <YearComparisonChartTooltip
                                            {...sharedTooltipProps}
                                            {...props}
                                            hideAmounts={customerViewToggled}
                                            label={t('radio_option__order_sum_margin', { ns: 'explorer' })}
                                            valueDisplayType="currency"
                                        />
                                    )}
                                    curveType="monotone"
                                />
                            </>
                        ) : (
                            <ChartLoading />
                        )}
                    </DashboardChatTabsItem>
                )}
            </DashboardChatTabs>
        </DashboardSection>
    );
}
